<script setup lang="ts">
import SchemaOrg from '@/shared/ui/SchemaOrg/index';
import AppSort from '@/shared/ui/AppSort/index';
import AppTabs from '@/shared/ui/AppTabs/index';
import JournalSearch from '@/pages/journal/ui/JournalSearch.vue';
import JournalCardSection from '@/pages/journal/ui/JournalCardSection.vue';
import JournalCardSections from '@/pages/journal/ui/JournalCardSections.vue';
import JournalPageSkeleton from '@/pages/journal/ui/Skeleton/index.vue';
import JournalCardSkeletonSection from '@/pages/journal/ui/Skeleton/JournalCardSkeletonSection.vue';
import useJournal from '@/pages/journal/composables/useJournal';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';
import { JOURNAL_PRIORITY_CARDS_COUNT } from '@/pages/journal/lib/useJournalConstants';

const {
  isMobile,
  isDetailPage,
  isShowSkeleton,
  sections,
  articles,
  topicName,
  topicDescription,
  breadcrumbs,
  breadcrumbSchemaData,
  webPageSchemaData,
  searchValue,
  selectedTab,
  tabList,
  topicsSearchResults,
  articlesSearchResults,
  foundResultsNumber,
  debouncedSearch,
  clearHandler,
  sortOptions,
  currentSort,
  pageReady,
  pageError,
} = useJournal();

const priorityCardIds: ComputedRef<number[]> = computed(() => {
  const firstCards = sections.value?.[0]?.journals ?? articles.value ?? [];
  return firstCards.slice(0, JOURNAL_PRIORITY_CARDS_COUNT).map((card) => card.id);
});

provide('priorityCardIds', priorityCardIds);

if (import.meta.server) {
  await pageReady;
  if (pageError.value) {
    throw createError({ statusCode: getErrorStatus(pageError.value), fatal: true });
  }
}
</script>

<template>
  <div :class="['journal-page', { _mobile: isMobile }, { _detail: isDetailPage }]">
    <SchemaOrg type="BreadcrumbList" :data="breadcrumbSchemaData" />
    <SchemaOrg type="CollectionPage" :data="webPageSchemaData" />
    <div v-if="breadcrumbs.length" class="journal-page__breadcrumbs">
      <AppBreadcrumbs :key="breadcrumbs.length" :breadcrumbs="breadcrumbs" />
    </div>
    <div v-if="!searchValue || isDetailPage" class="journal-page__title-wrapper">
      <h1 class="journal-page__title">
        {{ topicName }}
      </h1>
      <div
        v-if="isDetailPage && !searchValue && topicDescription"
        class="journal-page__subtitle"
      >
        {{ topicDescription }}
      </div>
    </div>
    <div class="journal-page__header">
      <div class="journal-page__search">
        <JournalSearch @input="debouncedSearch" @clear="clearHandler" />
      </div>
      <div v-if="isDetailPage" class="journal-page__sort">
        <AppSort
          :options="sortOptions"
          :model-value="currentSort"
          default-label="Дате публикации"
        />
      </div>
    </div>
    <div v-if="isShowSkeleton" class="journal-page__skeleton">
      <JournalCardSkeletonSection v-if="isDetailPage" is-detail-page />
      <JournalPageSkeleton v-else />
    </div>
    <div v-else class="journal-page__content">
      <div v-if="searchValue" class="journal-page__result">
        <h2 class="journal-page__title _result">
          Результат поиска по запросу "{{ searchValue }}" {{ foundResultsNumber }}
          найдено
        </h2>
        <template v-if="!isDetailPage">
          <AppTabs
            v-model="selectedTab"
            class="journal-page__tabs"
            max-width="146px"
            :tab-list="tabList"
          >
            <template #tab-extra="{ tab }">
              <span class="app-tabs__amount _circle">{{ tab.amount }}</span>
            </template>
            <template #tabPanel-1>
              <div class="journal-page__cards">
                <JournalCardSections
                  :sections-list="topicsSearchResults"
                  :is-detail-page="isDetailPage"
                  is-topics
                />
              </div>
            </template>
            <template #tabPanel-2>
              <div class="journal-page__cards">
                <JournalCardSection
                  :section="articlesSearchResults"
                  :is-detail-page="isDetailPage"
                />
              </div>
            </template>
          </AppTabs>
        </template>
        <template v-else>
          <div class="journal-page__cards">
            <JournalCardSection
              :section="articlesSearchResults"
              :is-detail-page="isDetailPage"
            />
          </div>
        </template>
      </div>
      <div v-else class="journal-page__cards-wrapper">
        <div v-if="!isDetailPage" class="journal-page__cards">
          <JournalCardSections :sections-list="sections" is-topics />
        </div>
        <div v-else class="journal-page__cards">
          <JournalCardSection :section="articles" :is-detail-page="isDetailPage" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.journal-page {
  .container-d();
  .flex-column(@size-x10);

  padding-bottom: 100px;

  &__breadcrumbs {
    margin-top: @size-x10;
  }

  &__content {
    .flex-column(@size-x10);
  }

  &__title-wrapper {
    .flex-column(@size-x3);
  }

  &__title {
    .h-offset-reset();

    font-size: 30px;
    line-height: 39px;
    font-weight: 700;

    &._result {
      font-size: @size-x6;
      line-height: 31px;
    }
  }

  &__subtitle {
    font-size: @size-x4;
    line-height: 21px;
    font-weight: 700;
    color: @color-silver-chalice;
    max-width: 765px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 205px;
  }

  &__search {
    width: 100%;
  }

  &__sort {
    position: relative;
    flex-shrink: 0;
  }

  &__result {
    .flex-column(@size-x5);
  }

  &__tabs {
    .flex-column(@size-x10);

    .app-tabs__list {
      gap: @size-x3;
    }

    &.app-tabs._default .app-tabs__item {
      font-size: 14px;
      line-height: 18px;
      font-weight: 600;
      color: @color-silver-chalice;
      border: 1px solid @color-gallery;
      border-radius: 6px;

      &._active {
        font-weight: 600;
        color: @color-elephant;
        box-shadow: none;
        border-color: @color-persimmon;
      }
    }
  }

  &._mobile {
    .container-m();

    padding-bottom: 80px;
  }

  &._mobile &__subtitle {
    font-weight: 700;
  }

  &._mobile &__header {
    flex-wrap: wrap;
    gap: @size-x5;
  }
}
</style>
